import { useEffect, useState } from "react"
import moment from "moment"

import { useTranslation } from "lib/i18n/translation"
import notation from "lib/products/notation"
import { FETCH_PRODUCT_METADATA_AND_DIRECTORS } from "lib/graphql"
import classification from "lib/tools/classification"
import { mapPeople, manageFilmVersions, manageTechnicals, restoredVersion } from "lib/tools/product"

import { Query } from "components/common"
import { FilmVersion } from "components/modals/film-version"

const FilmSheet = ({ film, loading, setDuration}) => {

	if (loading) return <Content film={film} loading={loading} />

	return (
		<Query
			query={FETCH_PRODUCT_METADATA_AND_DIRECTORS}
			options={{ variables: { id: film.id }, fetchPolicy: "network-only" }}
			renderLoading={() => (
				<Content film={film} loading={true} />
			)}
		>
			{({ cms: { products: { items: [details] } } }) => {
				const duration = details.extension.videos.items[0]?.duration

				if (duration & setDuration) {
					setDuration(duration)
				}

				return (
				<Content film={film} details={details} />
			)}}
		</Query>
	)
}

export default FilmSheet

const Content = ({ film, details, loading }) => {

	const [extras, setExtras] = useState(false)
	const { c } = useTranslation("common")

	const filmVersions = manageFilmVersions(details, c)
	const isRestoredVersion = restoredVersion(details, c)
	const technicals = manageTechnicals(details)

	return (
		<section>
			<h2 className="section-title icon-file">{c("single-film-strong")}</h2>
			<div className="splitted">
				<div
					className={`content ${loading ? "skeleton" : ""}`}
					dangerouslySetInnerHTML={{ __html: film.description }}
				/>
				<aside>
					{filmVersions.find(v => v.value !== c("none"))
						? <ul className="film-versions list">
							{filmVersions.map((filmVersion, index) => (
								filmVersion.tooltipValue
									? <FilmVersion key={`film-versions-${index}`} filmVersion={filmVersion} />
									: filmVersion.value === c("none")
										? (
											<li key={`film-versions-${index}`} className="unavailable">
												<span className={filmVersion.name}>{c(filmVersion.name)} </span>
												{c("none")}
											</li>
										)
										: (
											<li key={`film-versions-${index}`}>
												<span className={filmVersion.name}>{c(filmVersion.name)} </span>
												{filmVersion.value}
											</li>
										)
							))}
						</ul>
						: null
					}
					<h2 className={loading ? "skeleton" : ""}>{c("status-vod-copy")}</h2>
					<div className={`film-status ${loading ? "skeleton" : ""}`}>
						{isRestoredVersion ?
							<p><span className="alert restored">{c("restored-version")}</span></p>
							: null
						}
						<span className="notation" data-stars={notation(technicals, c)}>{technicals.copy}</span>
						<span>{technicals.copy || c("film-copy-not-set")}</span>
						{technicals.videoFormat || technicals.sound
							? <div className="equipment">
								[ {technicals.videoFormat
									? <><span>{technicals.videoFormat}</span> - </>
									: ""
								}{technicals.sound
									? <span>{technicals.sound}</span>
									: ""
								} ]
							</div>
							: null
						}
						{classification(film)}
					</div>
					{film.actors && film.actors.length
						? <div className="casting">
							<h2 className={loading ? "skeleton" : ""}>{c("casting")}</h2>
							<ul className={`list ${loading ? "skeleton" : ""}`}>
								{film.actors.map((a, index) => (
									<li key={`casting-${index}`}>
										<span>{a.name}{a.role !== "" ? " : " : ""}</span>{a.role}
									</li>
								))}
							</ul>
						</div>
						: null
					}
				</aside>
			</div>
			<a
				href="#"
				onClick={e => { e.preventDefault(); setExtras(!extras) }}
				title={c("show-datasheet")}
				rel="nofollow noopener"
				className={`collapsible-toggle icon-clipboard button ${loading ? "skeleton" : ""}`}
			>
				{c("credits-and-datasheet")}
			</a>
			<div id="technical-extras" className={`collapsible ${loading ? "skeleton" : ""} ${extras ? "active" : ""}`}>
				<ul className="casting columns">
					<li>
						<h2>{c("credits")}</h2>
						{details
							? <ul className="list">
								{mapPeople(details).map((p, index) => (
									<li key={`credits-${index}`}>
										<span>{c(p.role)} : </span>{p.people.join(', ')}
									</li>
								))}
							</ul>
							: null
						}
					</li>
					<li>
						<h2>{c("technical-information")}</h2>
						<ul className="list">
							<li><span>{c("printed-film-format")} : </span>{technicals.support}</li>
							{!!technicals.sound
								? <li><span>{c("original-sound-mix")} : </span>{technicals.sound}</li>
								: null
							}
							<li><span>{c("production")} : </span>{film.year}</li>
							{!!technicals.dateFrance && (
								<li>
									<span>{c("release-date-for-france")} : </span>
									{moment(technicals.dateFrance).format("D MMMM YYYY")}
								</li>
							)}
							<li><span>{c("category")} : </span>{technicals.filmType}</li>
							<li><span>{c("color")} : </span>{technicals.colors}</li>
							<li><span>{c("language")} : </span>{technicals.lang}</li>
							{technicals.subtitles && (
								<li><span>{c("subtitles")} : </span>{technicals.subtitles}</li>
							)}
						</ul>
					</li>
				</ul>
			</div>
		</section>
	)
}
